// ---------------------------------------------------------------------------
// MULTI-REGIÃO (V1) — helpers compartilhados client+server (SEM imports de
// servidor: este módulo roda no browser, em getServerSideProps e em /api).
//
// Contrato do backend (site-graphql/legacy-public):
//   query pública `regioes` → [{ id, nome, slug, cidadeIds, telefone, whatsapp,
//   email, seoTitle, seoDescription }] (ordenadas). Vazia/erro → tenant SEM
//   multi-região (todo o recurso degrada pro comportamento atual).
//
// Resolução da região ATIVA (prioridade):
//   1. path prefix  /r/<slug>
//   2. query        ?regiao=<slug>
//   3. cookie       site_regiao   (SÓ no cliente)
//   4. MATRIZ       (V2, só quando regioesConfig.modoEntrada === 'popup')
// Regra de CACHE (importante): o SSR compartilhado por host SÓ regionaliza a
// resposta quando a região vem de path/query (URLs distintas → chaves de CDN
// distintas). Região vinda SÓ de cookie é aplicada APENAS client-side
// (contatos/CTAs e buscas client-side) — nunca no HTML do SSR — pra não
// envenenar o cache SSR/CDN por host. A MATRIZ é determinística por tenant,
// então pode entrar no SSR sem envenenar nada.
//
// V2 (multi-filiais): a região passa a ter `filialId`/`matriz` + conteúdo
// próprio (vídeo/hero/endereço) e o tenant escolhe o MODO DE ENTRADA
// (`regioesConfig.modoEntrada`). Dois portões guardam TUDO que é novo:
//   regioes.length > 0   (já era da V1)   E   modoEntrada === 'popup' + matriz.
// Modo 'barra' = V1 byte a byte; tenant sem regiões = ZERO mudança.
// ---------------------------------------------------------------------------

export type Regiao = {
    id: number;
    nome: string;
    slug: string;
    cidadeIds: number[];
    telefone?: string | null;
    whatsapp?: string | null;
    email?: string | null;
    seoTitle?: string | null;
    seoDescription?: string | null;
    // ----- V2 (multi-filiais) — todos OPCIONAIS: enquanto o backend não expõe,
    // chegam undefined e o site se comporta exatamente como na V1.
    /** filial do CRM (sobressai.tbl_filial) por trás da região. null = região sem filial. */
    filialId?: number | null;
    /** região "sede": é a que a home `/` carrega no modo popup. No máx. 1 por tenant. */
    matriz?: boolean;
    videoUrl?: string | null;
    videoPosterUrl?: string | null;
    imagemUrl?: string | null;
    heroTitulo?: string | null;
    heroSubtitulo?: string | null;
    descricao?: string | null;
    endereco?: string | null;
    instagram?: string | null;
    // ----- "Nossa Empresa" por filial — OPCIONAIS como os da V2: backend sem o
    // campo ⇒ undefined ⇒ o bloco segue com a imagem/título gerais do site.
    /** foto do bloco "Nossa Empresa" (equipe/fachada da filial). */
    empresaImagemUrl?: string | null;
    /** título do bloco "Nossa Empresa" quando esta filial está ativa. */
    empresaTitulo?: string | null;
};

// Config de ENTRADA do site (raiz própria `regioesConfig` no contrato público).
//  • 'barra'  → comportamento V1 byte a byte (barra de chips no topo 4).
//  • 'popup'  → matriz carrega por default na home e o popup de filiais aparece.
//  • 'tela'   → tudo do 'popup' + TELA DE ESCOLHA na entrada: o visitante escolhe
//    a filial ANTES de ver o site (FilialTela). O popup do canto segue servindo
//    para trocar de filial depois.
export type RegiaoModoEntrada = 'barra' | 'popup' | 'tela';

export type RegioesConfig = {
    modoEntrada: RegiaoModoEntrada;
    lembrarDias: number;
    tituloPopup: string;
    subtituloPopup: string;
};

export const REGIOES_CONFIG_DEFAULT: RegioesConfig = {
    modoEntrada: 'barra',
    lembrarDias: 90,
    tituloPopup: '',
    subtituloPopup: '',
};

export const REGIAO_COOKIE = 'site_regiao';
export const REGIAO_COOKIE_MAX_AGE_SEC = 90 * 24 * 60 * 60; // 90 dias
// flag de "já vi o popup" — localStorage, separado do cookie de escolha (que
// SEMPRE reflete a região efetiva, inclusive o default = matriz).
export const FILIAL_POPUP_VISTO_KEY = 'filial_popup_visto';

// slug seguro: minúsculo, a-z0-9 e hífen (mesma família dos slugs de URL).
export const sanitizeRegiaoSlug = (value: unknown): string => {
    const raw = Array.isArray(value) ? value[0] : value;
    return String(raw || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, '')
        .slice(0, 64);
};

const toPositiveIntOrNull = (value: unknown): number | null => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const toPositiveIntArray = (value: unknown): number[] => {
    let source: unknown = value;
    if (typeof source === 'string') {
        try {
            source = JSON.parse(source);
        } catch {
            source = String(source)
                .split(',')
                .map((v) => v.trim());
        }
    }
    if (!Array.isArray(source)) return [];
    return source
        .map((v) => Number(v))
        .filter((v) => Number.isInteger(v) && v > 0);
};

// Normaliza a lista crua vinda do GraphQL (cidadeIds pode chegar como JSON
// string ou como array). Região sem slug ou sem cidades ainda vale pro seletor
// (contato/SEO), então só exigimos slug.
export const normalizeRegioes = (raw: any): Regiao[] => {
    if (!Array.isArray(raw)) return [];
    const seen = new Set<string>();
    const regioes: Regiao[] = [];
    for (const item of raw) {
        const slug = sanitizeRegiaoSlug(item?.slug);
        if (!slug || seen.has(slug)) continue;
        seen.add(slug);
        regioes.push({
            id: Number(item?.id) || 0,
            nome: String(item?.nome || slug),
            slug,
            cidadeIds: toPositiveIntArray(item?.cidadeIds),
            telefone: item?.telefone ? String(item.telefone) : '',
            whatsapp: item?.whatsapp ? String(item.whatsapp) : '',
            email: item?.email ? String(item.email) : '',
            seoTitle: item?.seoTitle ? String(item.seoTitle) : '',
            seoDescription: item?.seoDescription ? String(item.seoDescription) : '',
            // V2 — ausentes enquanto o backend não expõe (undefined → V1).
            filialId: toPositiveIntOrNull(item?.filialId),
            matriz: item?.matriz === true || item?.matriz === 1 || item?.matriz === '1',
            videoUrl: item?.videoUrl ? String(item.videoUrl) : '',
            videoPosterUrl: item?.videoPosterUrl ? String(item.videoPosterUrl) : '',
            imagemUrl: item?.imagemUrl ? String(item.imagemUrl) : '',
            heroTitulo: item?.heroTitulo ? String(item.heroTitulo) : '',
            heroSubtitulo: item?.heroSubtitulo ? String(item.heroSubtitulo) : '',
            descricao: item?.descricao ? String(item.descricao) : '',
            endereco: item?.endereco ? String(item.endereco) : '',
            instagram: item?.instagram ? String(item.instagram) : '',
            empresaImagemUrl: item?.empresaImagemUrl ? String(item.empresaImagemUrl).trim() : '',
            empresaTitulo: item?.empresaTitulo ? String(item.empresaTitulo).trim() : '',
        });
    }
    return regioes;
};

// Config de entrada — QUALQUER entrada torta cai no default 'barra'/90d (= V1).
export const normalizeRegioesConfig = (raw: any): RegioesConfig => {
    const modo = String(raw?.modoEntrada || '').trim().toLowerCase();
    const dias = Number(raw?.lembrarDias);
    return {
        modoEntrada: modo === 'popup' || modo === 'tela' ? modo : 'barra',
        // 0 = "não lembrar" (cookie de sessão + popup a cada visita). Só valor
        // NEGATIVO/inválido cai no default de 90 dias.
        lembrarDias: Number.isFinite(dias) && dias >= 0 && dias <= 3650 ? Math.floor(dias) : 90,
        tituloPopup: raw?.tituloPopup ? String(raw.tituloPopup).slice(0, 120) : '',
        subtituloPopup: raw?.subtituloPopup ? String(raw.subtituloPopup).slice(0, 255) : '',
    };
};

// Região MATRIZ do tenant (a "sede" que a home `/` carrega). Sem nenhuma
// marcada, devolve null — e o modo popup NÃO liga (cai na V1/barra).
export const getRegiaoMatriz = (regioes: Regiao[] | null | undefined): Regiao | null => {
    const lista = Array.isArray(regioes) ? regioes : [];
    return lista.find((r) => r.matriz === true) || null;
};

// PORTÃO do comportamento novo: precisa de regiões, modoEntrada='popup' (ou
// 'tela', que é o popup + a tela de escolha na entrada) E matriz.
// Sem matriz marcada o tenant fica na V1 (decisão assumida do plano).
export const isModoPopup = (
    regioes: Regiao[] | null | undefined,
    cfg: RegioesConfig | null | undefined
): boolean => {
    if (cfg?.modoEntrada !== 'popup' && cfg?.modoEntrada !== 'tela') return false;
    const lista = Array.isArray(regioes) ? regioes : [];
    if (!lista.length) return false;
    return Boolean(getRegiaoMatriz(lista));
};

// PORTÃO da TELA DE ESCOLHA: modoEntrada='tela' + tudo que o modo popup exige +
// 2 ou mais filiais (com uma só não há o que escolher).
export const isModoTela = (
    regioes: Regiao[] | null | undefined,
    cfg: RegioesConfig | null | undefined
): boolean => {
    if (cfg?.modoEntrada !== 'tela') return false;
    if (!isModoPopup(regioes, cfg)) return false;
    return (Array.isArray(regioes) ? regioes : []).length >= 2;
};

export const getRegiaoSlugFromPath = (path: unknown): string => {
    const pathname = String(path || '').split('?')[0].split('#')[0];
    const match = pathname.match(/^\/r\/([^/?#]+)\/?$/);
    return match ? sanitizeRegiaoSlug(decodeURIComponentSafe(match[1])) : '';
};

const decodeURIComponentSafe = (value: string): string => {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

// query pode vir como objeto (router.query / ctx.query) ou string ("a=1&b=2").
export const getRegiaoSlugFromQuery = (query: unknown): string => {
    if (!query) return '';
    if (typeof query === 'string') {
        const source = query.includes('?') ? query.split('?')[1] : query;
        try {
            return sanitizeRegiaoSlug(new URLSearchParams(source).get('regiao'));
        } catch {
            return '';
        }
    }
    return sanitizeRegiaoSlug((query as any)?.regiao);
};

export const getRegiaoSlugFromCookieString = (cookie: unknown): string => {
    const source = String(cookie || '');
    if (!source) return '';
    const found = source
        .split(';')
        .map((part) => part.trim())
        .find((part) => part.startsWith(`${REGIAO_COOKIE}=`));
    if (!found) return '';
    return sanitizeRegiaoSlug(decodeURIComponentSafe(found.slice(REGIAO_COOKIE.length + 1)));
};

export type RegiaoSignals = {
    path?: unknown;
    query?: unknown;
    cookie?: unknown;
};

// Slug bruto pela prioridade path > query > cookie (sem validar contra a lista).
export const resolveRegiaoSlug = ({ path, query, cookie }: RegiaoSignals): string =>
    getRegiaoSlugFromPath(path) ||
    getRegiaoSlugFromQuery(query) ||
    getRegiaoSlugFromQuery(String(path || '')) || // ?regiao= embutido no asPath
    getRegiaoSlugFromCookieString(cookie);

// Região ATIVA: o slug resolvido precisa casar com uma região do tenant.
// V2 (modo popup): sem sinal nenhum — ou com sinal que não casa — cai na MATRIZ.
// A cadeia de sinais é decidida por quem CHAMA: o servidor passa só `path`
// (SSR/CDN determinístico por URL), o cliente passa path+query+cookie.
export const resolveRegiaoAtiva = (
    regioes: Regiao[] | null | undefined,
    signals: RegiaoSignals,
    cfg?: RegioesConfig | null
): Regiao | null => {
    const lista = Array.isArray(regioes) ? regioes : [];
    if (!lista.length) return null;
    const slug = resolveRegiaoSlug(signals);
    const encontrada = slug ? lista.find((r) => r.slug === slug) || null : null;
    if (encontrada) return encontrada;
    if (isModoPopup(lista, cfg)) return getRegiaoMatriz(lista);
    return null;
};

// ------------------------- browser-only helpers ---------------------------

// Slug ativo lido direto dos sinais do browser (path, ?regiao=, cookie).
// Usado onde não há acesso ao contexto React (ex.: sendUser). Retorna '' no SSR.
export const getRegiaoSlugFromBrowser = (): string => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return '';
    return resolveRegiaoSlug({
        path: window.location.pathname,
        query: window.location.search,
        cookie: document.cookie,
    });
};

// Grava/limpa o cookie de região (client-side; path=/, 90d por default — o
// tenant pode encurtar/alongar por `regioesConfig.lembrarDias`).
export const writeRegiaoCookie = (
    slug: string | null | undefined,
    maxAgeSec?: number
): void => {
    if (typeof document === 'undefined') return;
    const safeSlug = sanitizeRegiaoSlug(slug);
    const pedido = Number(maxAgeSec);
    // lembrarDias = 0 → cookie de SESSÃO (sem max-age): a escolha vale só nesta
    // aba/visita e o popup reaparece na próxima.
    const deSessao = Number.isFinite(pedido) && pedido === 0;
    const maxAge =
        Number.isFinite(pedido) && pedido > 0 ? Math.floor(pedido) : REGIAO_COOKIE_MAX_AGE_SEC;
    if (!safeSlug) {
        document.cookie = `${REGIAO_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
        return;
    }
    const valor = `${REGIAO_COOKIE}=${encodeURIComponent(safeSlug)}; path=/; SameSite=Lax`;
    document.cookie = deSessao ? valor : `${valor}; max-age=${maxAge}`;
};

// "Já vi o popup" — flag SEPARADA do cookie (que sempre reflete a escolha
// efetiva, inclusive o default = matriz). Sem isto, gravar o default no cookie
// mataria a 1ª visita.
// Com `lembrarDias = 0` a flag vai pro sessionStorage: o popup volta a cada
// visita nova, que é o que "não lembrar" quer dizer.
const storageDaFlag = (lembrarDias?: number | null): Storage | null => {
    if (typeof window === 'undefined') return null;
    try {
        return Number(lembrarDias) === 0 ? window.sessionStorage : window.localStorage;
    } catch {
        return null; // storage bloqueado (private mode)
    }
};

export const lePopupFilialVisto = (lembrarDias?: number | null): boolean => {
    try {
        return storageDaFlag(lembrarDias)?.getItem(FILIAL_POPUP_VISTO_KEY) === '1';
    } catch {
        return false; // storage bloqueado → trata como 1ª visita
    }
};

export const marcaPopupFilialVisto = (lembrarDias?: number | null): void => {
    try {
        storageDaFlag(lembrarDias)?.setItem(FILIAL_POPUP_VISTO_KEY, '1');
    } catch {
        void 0; // storage bloqueado — o popup reabre na próxima visita, sem quebrar
    }
};

// ------------------- reabrir o popup sob demanda (?filial=escolher) --------

export const FILIAL_ESCOLHER_PARAM = 'filial';
export const FILIAL_ESCOLHER_VALOR = 'escolher';

// `?filial=escolher` em QUALQUER host (produção inclusive): serve pra re-testar
// a entrada do site sem ter que caçar o cookie no navegador.
export const querEscolherFilial = (search?: unknown): boolean => {
    const source = String(search || '');
    if (!source) return false;
    const queryString = source.includes('?') ? source.split('?')[1] : source;
    try {
        return new URLSearchParams(queryString).get(FILIAL_ESCOLHER_PARAM) === FILIAL_ESCOLHER_VALOR;
    } catch {
        return false;
    }
};

// Zera a escolha de filial: cookie + flag nos DOIS storages (a config pode ter
// mudado de lembrarDias entre uma visita e outra).
export const limpaEscolhaDeFilial = (): void => {
    writeRegiaoCookie(null);
    if (typeof window === 'undefined') return;
    for (const storage of ['localStorage', 'sessionStorage'] as const) {
        try {
            window[storage]?.removeItem(FILIAL_POPUP_VISTO_KEY);
        } catch {
            void 0; // storage bloqueado — nada a limpar
        }
    }
};

// ------------------- TELA DE ESCOLHA na entrada (modoEntrada='tela') -------
//
// O visitante escolhe a filial ANTES de ver o site. Quem decide se a tela abre é
// `deveAbrirTelaFilial` (regra pura, usada pelo componente) — e o script inline
// de `buildFilialTelaBootstrap` repete a MESMA regra em JS puro, no <head>, para
// cobrir a página antes da 1ª pintura (sem isso o site piscaria por baixo até o
// React hidratar).

// Rotas em que a tela NÃO abre: a ficha do imóvel é a mesma URL para todas as
// filiais (quem chega de portal/anúncio quer ver o imóvel), e páginas legais ou
// de erro não têm o que filtrar.
export const ROTAS_SEM_TELA_FILIAL_FONTES = [
    '(^|\\/)imovel(\\/|$)', // segmento em qualquer posição: /imoveis/venda/<cidade>/…/imovel/<id>
    '^\\/politica(\\/|$)',
    '^\\/termos(\\/|$)',
    '^\\/404(\\/|$)',
    '^\\/500(\\/|$)',
    '^\\/_error(\\/|$)',
];
const ROTAS_SEM_TELA_FILIAL = ROTAS_SEM_TELA_FILIAL_FONTES.map((fonte) => new RegExp(fonte, 'i'));

// Robô não escolhe filial: sem isto o render do Googlebot/Lighthouse sairia com
// a tela por cima do conteúdo (intersticial). O HTML do servidor não muda.
export const ROBO_UA_FONTE =
    'googlebot|bingbot|adsbot|storebot|google-inspectiontool|googleother|yandex|baiduspider|duckduckbot|applebot|ahrefsbot|semrushbot|crawl|spider|slurp|lighthouse|pagespeed|gtmetrix|headlesschrome|bingpreview|prerender';
const ROBO_UA = new RegExp(ROBO_UA_FONTE, 'i');

export type TelaFilialSinais = {
    modoTela: boolean;
    pathname: string;
    search?: string;
    jaViu: boolean;
    ehPagina404?: boolean;
    userAgent?: string;
};

// A URL já diz a filial (`/r/<slug>` ou `?regiao=`): link do Google, de campanha
// ou do rodapé de filiais — a escolha está feita.
export const urlJaDizAFilial = (pathname?: unknown, search?: unknown): boolean =>
    Boolean(getRegiaoSlugFromPath(pathname) || getRegiaoSlugFromQuery(search));

export const deveAbrirTelaFilial = ({
    modoTela,
    pathname,
    search,
    jaViu,
    ehPagina404,
    userAgent,
}: TelaFilialSinais): boolean => {
    if (!modoTela) return false;
    if (ehPagina404) return false;
    if (jaViu) return false;
    if (userAgent && ROBO_UA.test(userAgent)) return false;
    const caminho = String(pathname || '/');
    if (ROTAS_SEM_TELA_FILIAL.some((re) => re.test(caminho))) return false;
    if (urlJaDizAFilial(caminho, search)) return false;
    return true;
};

// "Já escolhi na tela" — além da flag do popup (storage), um COOKIE. Motivo: com
// `lembrarDias = 0` a flag mora no sessionStorage, que é POR ABA; link aberto em
// aba nova nasceria sem ela e a tela (que bloqueia o site) perguntaria de novo
// no meio da mesma visita. O cookie de sessão vale para todas as abas.
export const FILIAL_TELA_VISTA_COOKIE = 'filial_tela_vista';
const FILIAL_TELA_VISTA_RE_FONTE = `(?:^|;\\s*)${FILIAL_TELA_VISTA_COOKIE}=1(?:;|$)`;

export const leTelaFilialVista = (lembrarDias?: number | null): boolean => {
    if (lePopupFilialVisto(lembrarDias)) return true;
    if (typeof document === 'undefined') return false;
    return new RegExp(`(?:^|;\\s*)${FILIAL_TELA_VISTA_COOKIE}=1(?:;|$)`).test(document.cookie || '');
};

export const marcaTelaFilialVista = (lembrarDias?: number | null): void => {
    marcaPopupFilialVisto(lembrarDias);
    if (typeof document === 'undefined') return;
    const dias = Number(lembrarDias);
    const valor = `${FILIAL_TELA_VISTA_COOKIE}=1; path=/; SameSite=Lax`;
    // 0 (ou inválido) = cookie de SESSÃO: a tela volta na próxima visita
    document.cookie =
        Number.isFinite(dias) && dias > 0 ? `${valor}; max-age=${Math.floor(dias) * 86400}` : valor;
};

// `?filial=escolher`: zera tudo (cookie de região, flags e o cookie da tela).
export const limpaTelaFilialVista = (): void => {
    limpaEscolhaDeFilial();
    if (typeof document === 'undefined') return;
    document.cookie = `${FILIAL_TELA_VISTA_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
};

// Classe no <html> que MOSTRA a tela (o markup vem no HTML do servidor, sempre
// escondido). Quem liga antes da 1ª pintura é o script inline abaixo; depois da
// hidratação quem manda é o componente.
export const FILIAL_TELA_PENDENTE_CLASS = 'filial-tela-pendente';

// CSS + script INLINE do <head> (só no modo tela). O script decide com os
// MESMOS sinais de `deveAbrirTelaFilial` e, se a tela vai abrir, marca o <html>
// — assim ela já nasce na 1ª pintura, sem o site piscar por baixo até o React
// hidratar. Tudo sai de dados do TENANT (lembrarDias): o HTML é idêntico para
// todos os visitantes e segue cacheável. Sem prazo de segurança de propósito:
// os cards são <a href> reais, então a tela funciona mesmo se o JS não subir.
export const buildFilialTelaBootstrap = (
    lembrarDias: number | null | undefined
): { css: string; script: string } => {
    const storage = Number(lembrarDias) === 0 ? 'sessionStorage' : 'localStorage';
    const rotas = JSON.stringify(ROTAS_SEM_TELA_FILIAL_FONTES);
    const css = `html.${FILIAL_TELA_PENDENTE_CLASS}{overflow:hidden}`;
    const script =
        `(function(){try{` +
        // uma vez por carregamento de página (o head manager do Next pode
        // reinserir a tag numa navegação client-side)
        `if(window.__filialTelaBoot)return;window.__filialTelaBoot=1;` +
        `var l=location,p=l.pathname,q=l.search;` +
        `var forcar=/[?&]${FILIAL_ESCOLHER_PARAM}=${FILIAL_ESCOLHER_VALOR}(?:&|$)/.test(q);` +
        `if(!forcar){` +
        `if(/${ROBO_UA_FONTE}/i.test(navigator.userAgent||''))return;` +
        `var r=${rotas};for(var i=0;i<r.length;i++){if(new RegExp(r[i],'i').test(p))return;}` +
        `if(/^\\/r\\/[^/]+/.test(p)||/[?&]regiao=[^&]/.test(q))return;` +
        `var v=null;try{v=window.${storage}.getItem('${FILIAL_POPUP_VISTO_KEY}');}catch(e){}` +
        `if(v==='1'||/${FILIAL_TELA_VISTA_RE_FONTE}/.test(document.cookie))return;` +
        `}` +
        `document.documentElement.classList.add('${FILIAL_TELA_PENDENTE_CLASS}');` +
        `}catch(e){}})();`;
    return { css, script };
};

// O cadastro de cidades de muitos tenants está em CAIXA ALTA ("CAPÃO DA CANOA").
// Só nesse caso vira nome próprio ("Capão da Canoa"); o que já vem em caixa
// mista fica como está. Hífen não abre maiúscula ("Xangri-lá").
const PARTICULAS_CIDADE = new Set(['de', 'da', 'do', 'das', 'dos', 'e']);
export const nomeDeCidadeLegivel = (bruto: unknown): string => {
    const nome = String(bruto ?? '').trim();
    if (!nome || nome !== nome.toUpperCase()) return nome;
    return nome
        .toLowerCase()
        .split(/\s+/)
        .map((palavra, i) =>
            i > 0 && PARTICULAS_CIDADE.has(palavra)
                ? palavra
                : palavra.charAt(0).toUpperCase() + palavra.slice(1)
        )
        .join(' ');
};

// Cidades que a filial atende, para o card da tela de escolha: nomes sem a UF,
// sem repetição, no máximo `max` + quantas ficaram de fora.
export const cidadesDaRegiao = (
    cidades: any[] | null | undefined,
    regiao: Regiao | null | undefined,
    max = 3
): { nomes: string[]; restantes: number } => {
    const lista = Array.isArray(cidades) ? cidades : [];
    const ids = Array.isArray(regiao?.cidadeIds) ? regiao!.cidadeIds : [];
    if (!lista.length || !ids.length) return { nomes: [], restantes: 0 };

    const permitidos = new Set(ids.map((id) => Number(id)));
    const vistos = new Set<string>();
    const nomes: string[] = [];
    for (const cidade of lista) {
        const id = Number((cidade as any)?.value ?? (cidade as any)?.id);
        if (!permitidos.has(id)) continue;
        const nome = nomeDeCidadeLegivel(
            String((cidade as any)?.nome ?? (cidade as any)?.label ?? '').split(' - ')[0]
        );
        if (!nome || vistos.has(nome.toLowerCase())) continue;
        vistos.add(nome.toLowerCase());
        nomes.push(nome);
    }

    const teto = Number.isFinite(Number(max)) && Number(max) > 0 ? Math.floor(Number(max)) : 3;
    return { nomes: nomes.slice(0, teto), restantes: Math.max(0, nomes.length - teto) };
};

// ------------------------- contato regional -------------------------------

export type ContatoRegiao = {
    telefone: string;
    whatsapp: string;
    email: string;
    regional: boolean; // true quando a região sobrescreveu o contato
};

// Contato efetivo: se a região tem telefone/whatsapp, eles vencem; senão os
// Dados normais do site (tel1 & cia).
export const resolveContatoRegiao = (site: any, regiao: Regiao | null | undefined): ContatoRegiao => {
    const dados = site?.Dados || {};
    const regional = Boolean(regiao && (regiao.telefone || regiao.whatsapp || regiao.email));
    if (!regional) {
        return {
            telefone: String(dados?.tel1 || ''),
            whatsapp: dados?.tel1_whats ? String(dados?.tel1 || '') : '',
            email: String(dados?.email1 || ''),
            regional: false,
        };
    }
    return {
        telefone: String(regiao?.telefone || regiao?.whatsapp || dados?.tel1 || ''),
        whatsapp: String(regiao?.whatsapp || ''),
        email: String(regiao?.email || dados?.email1 || ''),
        regional: true,
    };
};

// Clona o `site` trocando os Dados de contato pelos da região (quando existem).
// Mantém a MESMA estrutura consumida pelos componentes (tel1..4/telN_whats/
// telN_nome/email1), então topo/FAB/drawer/rodapé regionalizam sem reescrever
// cada componente. Quando a região define contato, tel3/tel4 são limpos pra
// não misturar números globais com regionais.
export const aplicaContatoRegiao = (site: any, regiao: Regiao | null | undefined): any => {
    if (!site || !regiao) return site;
    const temContato = Boolean(regiao.telefone || regiao.whatsapp);
    const temEmail = Boolean(regiao.email);
    if (!temContato && !temEmail) return site;

    const dados = { ...(site.Dados || {}) };
    if (temContato) {
        const principal = String(regiao.whatsapp || regiao.telefone || '');
        const secundario =
            regiao.whatsapp && regiao.telefone && regiao.telefone !== regiao.whatsapp
                ? String(regiao.telefone)
                : '';
        dados.tel1 = principal;
        dados.tel1_whats = Boolean(regiao.whatsapp);
        dados.tel1_nome = regiao.nome || dados.tel1_nome || 'WhatsApp';
        dados.tel2 = secundario;
        dados.tel2_whats = false;
        dados.tel2_nome = secundario ? regiao.nome || dados.tel2_nome || 'Comercial' : dados.tel2_nome;
        dados.tel3 = '';
        dados.tel4 = '';
    }
    if (temEmail) {
        dados.email1 = String(regiao.email);
    }
    return { ...site, Dados: dados };
};

// ------------------------- "Nossa Empresa" regional ------------------------

// Clona o `banner_empresa` (o `allOwners` das seções "Nossa Empresa") trocando
// a foto e/ou o título pelos da filial ativa. Mesma ideia do aplicaContatoRegiao:
// a ESTRUTURA consumida pelos componentes não muda (`linkbanner`/`card_titulo`),
// então todos os modelos regionalizam sem reescrever cada seção.
// Filial sem foto/título próprios (ou sem filial ativa) ⇒ devolve o MESMO objeto
// (identidade preservada: zero re-render, zero diferença pros tenants sem o campo).
// Tenant sem bloco cadastrado (`banner_empresa` null) continua sem bloco: a foto
// da filial troca a do bloco, não cria um bloco que a imobiliária não montou.
export const aplicaEmpresaRegiao = <T,>(empresa: T, regiao: Regiao | null | undefined): T => {
    if (!empresa || !regiao) return empresa;
    const imagem = String(regiao.empresaImagemUrl || '').trim();
    const titulo = String(regiao.empresaTitulo || '').trim();
    if (!imagem && !titulo) return empresa;
    return {
        ...(empresa as any),
        ...(imagem ? { linkbanner: imagem } : null),
        ...(titulo ? { card_titulo: titulo } : null),
    };
};

// ------------------------- links regionais (V2) ---------------------------

// Acrescenta `?regiao=<slug>` a um link INTERNO para a região sobreviver à
// navegação. NO-OP (devolve o href intacto) quando:
//  • href externo/âncora/mailto/tel — ou vazio;
//  • o path já é `/r/<slug>` (a região está no caminho);
//  • é ficha de imóvel (`/imovel/...`) — NUNCA ganha o parâmetro (SEO: a ficha
//    é a mesma URL para todas as filiais);
//  • não há região ativa, ou a ativa é a MATRIZ (matriz = URL limpa);
//  • o href já carrega `regiao=`.
export const appendRegiaoQuery = (
    href: string | null | undefined,
    regiao: Regiao | null | undefined,
    matrizSlug?: string | null
): string => {
    const raw = String(href ?? '');
    if (!raw || !raw.startsWith('/')) return raw; // externo, âncora, mailto:, tel:
    const slug = sanitizeRegiaoSlug(regiao?.slug);
    if (!slug) return raw;
    if (regiao?.matriz === true) return raw;
    if (matrizSlug && slug === sanitizeRegiaoSlug(matrizSlug)) return raw;

    const [semHash, ...hashParts] = raw.split('#');
    const hash = hashParts.length ? `#${hashParts.join('#')}` : '';
    const [pathname, ...queryParts] = semHash.split('?');
    const queryString = queryParts.join('?');

    if (/^\/r(\/|$)/.test(pathname)) return raw; // já regionalizado pelo caminho
    if (/(^|\/)imovel(\/|$)/.test(pathname)) return raw; // ficha de imóvel: nunca
    if (/(^|[?&])regiao=/.test(queryString)) return raw; // já tem

    const novaQuery = queryString ? `${queryString}&regiao=${slug}` : `regiao=${slug}`;
    return `${pathname}?${novaQuery}${hash}`;
};

// ------------------------- recortes por região (V2) -----------------------

// Cidades do select restritas às da região. Aceita tanto a lista CRUA do
// payload (`{id,nome}`) quanto a já formatada por formatSelects (`{value,label}`).
// FAIL-OPEN: se o recorte ficar VAZIO (região com ids órfãos/desatualizados),
// devolve a lista inteira — melhor mostrar todas as cidades do que entregar um
// select vazio que impede o visitante de buscar.
export const filtraCidadesRegiao = <T,>(
    cidades: T[] | null | undefined,
    regiao: Regiao | null | undefined
): T[] => {
    const lista = Array.isArray(cidades) ? cidades : [];
    const ids = Array.isArray(regiao?.cidadeIds) ? regiao!.cidadeIds : [];
    if (!lista.length || !ids.length) return lista;
    const permitidos = new Set(ids);
    const filtradas = lista.filter((c: any) => permitidos.has(Number(c?.value ?? c?.id)));
    return filtradas.length ? filtradas : lista;
};

// Equipe da região = corretores da FILIAL vinculada à região.
//  • região SEM `filialId` → todos os corretores (decisão do plano);
//  • corretor SEM filial nenhuma → aparece em TODAS as regiões (gestor/diretoria);
//  • `filialIdsPorParceiro` vem do documento fail-safe `regioesSite` (mesclado
//    por id); quando ausente, cai no `filialIds` do próprio parceiro, e sem
//    nenhum dos dois o filtro é NO-OP (backend ainda sem o campo → V1).
export const filtraEquipeRegiao = <T,>(
    parceiros: T[] | null | undefined,
    regiao: Regiao | null | undefined,
    filialIdsPorParceiro?: Record<string, number[]> | null
): T[] => {
    const lista = Array.isArray(parceiros) ? parceiros : [];
    const filialAlvo = Number(regiao?.filialId) || 0;
    if (!lista.length || !filialAlvo) return lista;

    const mapa = filialIdsPorParceiro || null;
    let algumTemFilial = false;
    const filiaisDe = (p: any): number[] => {
        const doMapa = mapa ? mapa[String(p?.id)] : undefined;
        const brutas = Array.isArray(doMapa) ? doMapa : p?.filialIds;
        const ids = toPositiveIntArray(brutas);
        if (ids.length) algumTemFilial = true;
        return ids;
    };

    const filtrados = lista.filter((p: any) => {
        const ids = filiaisDe(p);
        return ids.length === 0 || ids.includes(filialAlvo);
    });

    // Ninguém tem vínculo de filial ⇒ contrato ainda não chegou (ou tenant não
    // preencheu): NO-OP, mostra a equipe inteira em vez de "todo mundo em todas".
    if (!algumTemFilial) return lista;
    return filtrados;
};

// ------------------------- site regionalizado (V2) ------------------------

// `site` da região = contato regional (V1) + vídeo/título/subtítulo do hero.
// Sem região (ou região sem NENHUM desses campos) devolve o MESMO objeto —
// zero efeito em tenant mono-região.
export const aplicaRegiao = (site: any, regiao: Regiao | null | undefined): any => {
    if (!site || !regiao) return site;
    const comContato = aplicaContatoRegiao(site, regiao);

    const video = String(regiao.videoUrl || '').trim();
    const heroTitulo = String(regiao.heroTitulo || '').trim();
    const heroSubtitulo = String(regiao.heroSubtitulo || '').trim();
    if (!video && !heroTitulo && !heroSubtitulo) return comContato;

    const out = { ...comContato };
    if (video) {
        out.url_video = video;
        // Marca de origem: os heros usam isto para NÃO derivar a variante mobile
        // `_b.mp4` (que não existe para vídeo regional) — ver welcome/welcome3.
        out.url_video_regional = true;
        const poster = String(regiao.videoPosterUrl || '').trim();
        out.url_video_poster = poster || '';
    }
    if (heroTitulo) out.frase_busca = heroTitulo;
    if (heroSubtitulo) out.frase_busca_2 = heroSubtitulo;
    return out;
};

// ------------------------- args de GraphQL (V2) ---------------------------

// Trecho `,id_cidades: [..]` do contrato público (as queries do site são
// montadas por STRING). Sanitiza para inteiros positivos e devolve '' quando
// não há ids — assim a query de um tenant sem região sai IDÊNTICA à de hoje.
export const buildIdCidadesGraphqlArg = (idCidades?: number[] | null): string => {
    const ids = toPositiveIntArray(idCidades);
    return ids.length > 0 ? `,id_cidades: [${ids.join(',')}]` : '';
};

// Script INLINE do <head> da home `/` (só no modo popup). Se o visitante já tem
// cookie de uma filial que NÃO é a matriz, manda a navegação para `/r/<slug>`
// antes da hidratação. Os dados vêm do payload do TENANT (lista de slugs +
// matriz), nunca do cookie ⇒ o HTML continua idêntico para todo mundo e
// cacheável na CDN. Bot não tem cookie ⇒ bot nunca é redirecionado.
export const buildRegiaoBootstrapScript = (
    slugs: Array<string | null | undefined>,
    matrizSlug: string | null | undefined
): string => {
    const lista = (Array.isArray(slugs) ? slugs : [])
        .map((s) => sanitizeRegiaoSlug(s))
        .filter(Boolean);
    const matriz = sanitizeRegiaoSlug(matrizSlug);
    if (!lista.length || !matriz) return '';
    // slugs já sanitizados a [a-z0-9-] ⇒ impossível fechar a tag <script>.
    const listaJson = JSON.stringify(lista);
    return (
        `(function(){try{` +
        // ?filial=escolher re-testa a entrada do site: NÃO redirecionar, o popup
        // abre nesta mesma página depois de limpar cookie e flag.
        `if(/[?&]${FILIAL_ESCOLHER_PARAM}=${FILIAL_ESCOLHER_VALOR}(?:&|$)/.test(location.search))return;` +
        `var m=document.cookie.match(/(?:^|;\\s*)${REGIAO_COOKIE}=([^;]*)/);if(!m)return;` +
        `var s=decodeURIComponent(m[1]).toLowerCase().replace(/[^a-z0-9-]/g,'');` +
        `if(!s||s==='${matriz}')return;` +
        `if(${listaJson}.indexOf(s)<0)return;` +
        `location.replace('/r/'+s+location.search+location.hash);` +
        `}catch(e){}})();`
    );
};

// ------------------- busca em VÁRIAS cidades (multi-filiais) ---------------
//
// A listagem aceita `?cidades=6324,6319` (ids) além do slug de cidade única do
// caminho — que continua existindo para compatibilidade e SEO. Quem decide o
// que vale é `resolveCidadesBusca`, fonte ÚNICA da precedência:
//
//   cidades explícitas (2+) > cidade explícita (slug ou 1 marcada) > região ativa
//
// A mesma função serve o cliente (montagem da URL e dos args da busca) e o SSR
// (contagem `ssr:imoveiscount` e o noindex de listagem vazia), então os três
// nunca divergem.

export const CIDADES_QUERY_PARAM = 'cidades';
// teto defensivo: a query vai por STRING e o arg é um IN — sem teto, uma URL
// forjada com milhares de ids viraria uma query gigante no upstream.
export const MAX_CIDADES_BUSCA = 30;

export const parseCidadesQuery = (value: unknown): number[] => {
    const raw = Array.isArray(value) ? value[0] : value;
    const texto = String(raw ?? '').trim();
    if (!texto) return [];
    const vistos = new Set<number>();
    const ids: number[] = [];
    for (const parte of texto.split(',')) {
        const n = Number(String(parte).trim());
        if (!Number.isInteger(n) || n <= 0 || vistos.has(n)) continue;
        vistos.add(n);
        ids.push(n);
        if (ids.length >= MAX_CIDADES_BUSCA) break;
    }
    return ids;
};

export const formatCidadesQuery = (ids?: Array<number | string> | null): string =>
    parseCidadesQuery((Array.isArray(ids) ? ids : []).join(',')).join(',');

export type OrigemCidadesBusca = 'multi' | 'cidade' | 'regiao' | 'nenhuma';

export type CidadesBusca = {
    /** `id_cidade` escalar do contrato (uma cidade só). */
    idCidade: number | null;
    /** `id_cidades` do contrato (lista IN). Nunca vem junto com idCidade. */
    idCidades: number[] | null;
    origem: OrigemCidadesBusca;
};

export const resolveCidadesBusca = ({
    cidadesSelecionadas,
    idCidade,
    regiao,
}: {
    /** ids marcados pelo visitante (select multi ou `?cidades=`). */
    cidadesSelecionadas?: Array<number | string> | null;
    /** cidade única explícita (slug do caminho / select simples). */
    idCidade?: number | string | null;
    regiao?: Regiao | null;
}): CidadesBusca => {
    const multi = parseCidadesQuery((Array.isArray(cidadesSelecionadas) ? cidadesSelecionadas : []).join(','));
    // 2+ cidades: é o caso novo — a lista IN vence tudo.
    if (multi.length >= 2) return { idCidade: null, idCidades: multi, origem: 'multi' };
    // 1 cidade marcada = mesmo caso da cidade explícita de sempre (URL com slug).
    if (multi.length === 1) return { idCidade: multi[0], idCidades: null, origem: 'cidade' };

    const unica = Number(idCidade);
    if (Number.isInteger(unica) && unica > 0) return { idCidade: unica, idCidades: null, origem: 'cidade' };

    const daRegiao = Array.isArray(regiao?.cidadeIds) ? regiao!.cidadeIds : [];
    if (daRegiao.length) return { idCidade: null, idCidades: daRegiao, origem: 'regiao' };

    return { idCidade: null, idCidades: null, origem: 'nenhuma' };
};

// Sufixo de chave de cache do recorte de cidades. '' quando não há recorte —
// tenant/rota sem região e sem multi grava exatamente a chave de hoje.
export const cidadesCacheSuffix = (busca: CidadesBusca, slugRegiao?: string | null): string => {
    if (busca.origem === 'multi') return `:c:${(busca.idCidades || []).join('-')}`;
    if (busca.origem === 'regiao') return `:r:${sanitizeRegiaoSlug(slugRegiao)}`;
    return '';
};

// ------------------- empreendimentos por filial ---------------------------
//
// A query pública `empreendimentos` NÃO aceita recorte por cidade (só `nome` e
// `mostrar_site`), mas o payload já traz `id_cidade` de cada empreendimento —
// então o recorte é feito aqui, em memória, sobre a MESMA leitura cacheada em
// `ssr:company:<host>:<codsite>` (nenhuma chave nova: o payload cru é o mesmo
// para todas as filiais; quem separa as filiais é a URL/HTML, /r/<slug>).
export const filtraEmpreendimentosRegiao = <T,>(
    empreendimentos: T[] | null | undefined,
    regiao: Regiao | null | undefined
): T[] => {
    const lista = Array.isArray(empreendimentos) ? empreendimentos : [];
    const ids = Array.isArray(regiao?.cidadeIds) ? regiao!.cidadeIds : [];
    if (!lista.length || !ids.length) return lista;

    const permitidos = new Set(ids);
    let algumTemCidade = false;
    const dentro = lista.filter((item: any) => {
        const idCidade = Number(item?.id_cidade);
        if (Number.isInteger(idCidade) && idCidade > 0) algumTemCidade = true;
        return permitidos.has(idCidade);
    });

    // Nenhum empreendimento do tenant tem cidade cadastrada ⇒ o dado não permite
    // recorte: NO-OP (melhor mostrar todos do que esvaziar a seção por falta de
    // cadastro no CRM).
    if (!algumTemCidade) return lista;
    // Fail-CLOSED de propósito: a filial sem empreendimento NÃO deve exibir os
    // das outras — é exatamente a queixa que originou o recorte.
    return dentro;
};
